// Helpers del GESTOR DOCUMENTAL (subida y descarga de archivos vía StoragePort).
// La subida va a POST /documentos (multipart) y devuelve el `path` que se guarda
// como *_key / storage_key en las acciones. La descarga de reservados se hace por
// el proxy autenticado GET /documentos/descargar?path=...

import { api, subir, descargarBlob } from './api';

// Enum CarpetaDocumental (backend). Debe coincidir con el contrato de la API.
export type CarpetaDocumental =
  | 'QUEJA'
  | 'ANEXOS'
  | 'INDAGACION'
  | 'INVESTIGACION'
  | 'PRUEBAS'
  | 'ALEGATOS'
  | 'PLIEGO'
  | 'JUZGAMIENTO_ORDINARIO'
  | 'JUZGAMIENTO_VERBAL'
  | 'RECURSOS'
  | 'SEGUNDA_INSTANCIA'
  | 'EJECUCION'
  | 'ARCHIVO_HISTORICO';

// Etiquetas en español para las carpetas documentales.
export const CARPETA_DOCUMENTAL: Record<CarpetaDocumental, string> = {
  QUEJA: 'Notificación disciplinaria',
  ANEXOS: 'Anexos',
  INDAGACION: 'Indagación previa',
  INVESTIGACION: 'Investigación',
  PRUEBAS: 'Pruebas',
  ALEGATOS: 'Alegatos',
  PLIEGO: 'Pliego de cargos',
  JUZGAMIENTO_ORDINARIO: 'Juzgamiento ordinario',
  JUZGAMIENTO_VERBAL: 'Juzgamiento verbal',
  RECURSOS: 'Recursos',
  SEGUNDA_INSTANCIA: 'Segunda instancia',
  EJECUCION: 'Ejecución de sanciones',
  ARCHIVO_HISTORICO: 'Archivo histórico',
};

// Lista de carpetas para poblar selects.
export const CARPETAS: CarpetaDocumental[] = Object.keys(
  CARPETA_DOCUMENTAL,
) as CarpetaDocumental[];

export function etiquetaCarpeta(carpeta?: string): string {
  if (!carpeta) return '—';
  return CARPETA_DOCUMENTAL[carpeta as CarpetaDocumental] ?? carpeta;
}

// Respuesta de POST /documentos.
export interface DocumentoSubido {
  path: string;
  filename: string;
  url: string;
  hash: string;
  id?: string;
}

// Documento listado por GET /expedientes/:id/documentos.
export interface DocumentoExpediente {
  id: string;
  carpeta: string;
  nombre: string;
  tipo_documento?: string | null;
  storage_key: string;
  hash_documento?: string | null;
  version?: number | null;
  fecha_carga?: string | null;
  url?: string | null;
}

export interface SubirDocumentoInput {
  file: File;
  carpeta?: CarpetaDocumental;
  expedienteId?: string;
  nombre?: string;
  tipoDocumento?: string;
  documento?: string; // id del documento dueño (para versionado)
}

/** Sube un archivo a POST /documentos (multipart) y devuelve sus metadatos. */
export async function subirDocumento({
  file,
  carpeta,
  expedienteId,
  nombre,
  tipoDocumento,
  documento,
}: SubirDocumentoInput): Promise<DocumentoSubido> {
  const form = new FormData();
  form.append('file', file);
  if (carpeta) form.append('carpeta', carpeta);
  if (expedienteId) form.append('expedienteId', expedienteId);
  if (nombre) form.append('nombre', nombre);
  if (tipoDocumento) form.append('tipoDocumento', tipoDocumento);
  if (documento) form.append('documento', documento);
  return subir<DocumentoSubido>('/documentos', form);
}

/** Lista los documentos de un expediente (gestor documental). */
export function listarDocumentos(
  expedienteId: string,
): Promise<DocumentoExpediente[]> {
  return api<DocumentoExpediente[]>(
    `/expedientes/${expedienteId}/documentos`,
  );
}

/**
 * Descarga (por proxy autenticado) el binario reservado identificado por su
 * `path`/storage_key y lo abre en una nueva pestaña del navegador.
 */
export async function descargarDocumento(
  path: string,
  nombre?: string,
): Promise<void> {
  const blob = await descargarBlob(
    `/documentos/descargar?path=${encodeURIComponent(path)}`,
  );
  const objectUrl = URL.createObjectURL(blob);
  if (nombre) {
    // Fuerza la descarga con el nombre del archivo.
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    // Abre el documento en una nueva pestaña.
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
  }
  // Libera el objeto tras un breve margen (la pestaña ya lo cargó).
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
